import { useState, useEffect, useRef } from "react";
import axios from "axios";

const DIAS_OPS = [
    { key: 15, label: "15 días" },
    { key: 30, label: "30 días" },
];

// Excluimos "hold" de la gráfica apilada para no mezclar transitorios con estados reales
const SERIES = [
    { key: "confirmado", label: "Confirmadas",        color: "var(--azul-logo-claro)" },
    { key: "completado", label: "Completadas",        color: "var(--verde-esmeralda)" },
    { key: "cancelado",  label: "Canceladas",         color: "var(--rojo-600)" },
    { key: "expirado",   label: "Expiradas",          color: "var(--gris-400)" },
];

const CONFIG_EVENTOS = {
    iniciar_reserva:    { label: "Iniciaron reserva",   desc: "Usuarios que abrieron el flujo de pago al hacer clic en «Reservar».",             color: "var(--azul-logo-claro)", icono: "bi-cart-plus"        },
    reserva_completada: { label: "Completaron el pago", desc: "Pagos aprobados por Wompi — la reserva quedó confirmada.",                        color: "var(--verde-esmeralda)", icono: "bi-check-circle-fill" },
    abandonar_reserva:  { label: "Abandonaron el pago", desc: "Usuarios que iniciaron el flujo de pago pero no llegaron a completar la compra.", color: "var(--rojo-600)", icono: "bi-x-circle"          },
};

function fmtFecha(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
    return `${Number(d)} ${meses[Number(m) - 1]}`;
}

function fmtFechaCompleta(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    return `${Number(d)} de ${meses[Number(m) - 1]} de ${y}`;
}

function fmtPeso(n) {
    if (!n && n !== 0) return "—";
    return "$" + new Intl.NumberFormat("es-CO").format(Math.round(n));
}

// ── Gráfica de barras apiladas SVG ────────────────────────────────────────────
function StackedBarChart({ serie, dias }) {
    const svgRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    const W = 1000, H = 240, PAD_L = 36, PAD_R = 12, PAD_T = 16, PAD_B = 36;
    const AREA_W = W - PAD_L - PAD_R;
    const AREA_H = H - PAD_T - PAD_B;

    const maxTotal  = Math.max(...serie.map(d => SERIES.reduce((s, k) => s + (d[k.key] || 0), 0)), 1);
    const gridLines = 4;
    const barWidth  = AREA_W / serie.length;
    const barPad    = barWidth * 0.2;
    const barActual = barWidth - barPad * 2;
    const labelCada = dias <= 15 ? 3 : 5;
    const GAP       = 1;

    function xBar(i) { return PAD_L + i * barWidth + barPad; }

    const handleMouseMove = (e) => {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const relX = (e.clientX - rect.left) * (W / rect.width) - PAD_L;
        const idx  = Math.floor(relX / barWidth);
        if (idx < 0 || idx >= serie.length) { setTooltip(null); return; }
        setTooltip({ svgX: xBar(idx) + barActual / 2, idx });
    };

    return (
        <div style={{ position: "relative" }}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
                style={{ width: "100%", height: "auto", display: "block", userSelect: "none" }}
                onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>

                {Array.from({ length: gridLines + 1 }, (_, i) => {
                    const y   = PAD_T + (i / gridLines) * AREA_H;
                    const val = Math.round(maxTotal * (1 - i / gridLines));
                    return (
                        <g key={i}>
                            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="var(--color-track-bg)" strokeWidth="1" />
                            {val > 0 && <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="11" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">{val}</text>}
                        </g>
                    );
                })}

                {serie.map((d, i) => {
                    const sel      = tooltip?.idx === i;
                    const dayTotal = SERIES.reduce((s, k) => s + (d[k.key] || 0), 0);
                    let cumPx = 0;
                    return (
                        <g key={d.fecha} opacity={tooltip && !sel ? 0.45 : 1} style={{ transition: "opacity 0.1s" }}>
                            {SERIES.map(({ key, color }) => {
                                const val = d[key] || 0;
                                if (val === 0) return null;
                                const hPx = (val / maxTotal) * AREA_H;
                                const y   = PAD_T + AREA_H - cumPx - hPx;
                                cumPx    += hPx + GAP;
                                const isTop = key === [...SERIES].reverse().find(s => (d[s.key] || 0) > 0)?.key;
                                return (
                                    <rect key={key}
                                        x={xBar(i)} y={y}
                                        width={barActual} height={Math.max(hPx - GAP, 1)}
                                        rx={isTop ? 3 : 0} ry={isTop ? 3 : 0}
                                        fill={color} />
                                );
                            })}
                            {dayTotal > 0 && (
                                <rect x={xBar(i)} y={PAD_T + AREA_H - (dayTotal / maxTotal) * AREA_H}
                                    width={barActual} height={(dayTotal / maxTotal) * AREA_H}
                                    fill="transparent"
                                    stroke={sel ? "var(--color-track-bg)" : "none"} strokeWidth="1.5" rx="3" ry="3" />
                            )}
                        </g>
                    );
                })}

                {serie.map((d, i) => {
                    if (i % labelCada !== 0 && i !== serie.length - 1) return null;
                    return (
                        <text key={`lx-${i}`} x={xBar(i) + barActual / 2} y={H - 6}
                            textAnchor="middle" fontSize="10.5" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">
                            {fmtFecha(d.fecha)}
                        </text>
                    );
                })}

                {tooltip && (
                    <line x1={tooltip.svgX} y1={PAD_T} x2={tooltip.svgX} y2={PAD_T + AREA_H}
                        stroke="var(--color-track-bg)" strokeWidth="1" strokeDasharray="4 3" />
                )}
            </svg>

            {tooltip && (() => {
                const d = serie[tooltip.idx];
                if (!d) return null;
                const total = SERIES.reduce((s, k) => s + (d[k.key] || 0), 0);
                return (
                    <div style={{
                        position: "absolute", top: 8,
                        left: `calc(${(tooltip.svgX / W) * 100}% + 8px)`,
                        transform: tooltip.svgX > W * 0.65 ? "translateX(calc(-100% - 16px))" : "none",
                        background: "var(--gris-900)", color: "var(--blanco)", borderRadius: 10,
                        padding: "8px 14px", fontSize: 12, pointerEvents: "none",
                        whiteSpace: "nowrap", zIndex: 10, boxShadow: "0 4px 16px rgba(var(--negro-rgb), 0.22)",
                        minWidth: 160,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{fmtFechaCompleta(d.fecha)}</div>
                        {SERIES.map(({ key, label, color }) => {
                            const v = d[key] || 0;
                            return (
                                <div key={key} className="d-flex justify-content-between gap-3" style={{ marginBottom: 3 }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }}></div>
                                        <span style={{ color: "rgba(var(--blanco-rgb), 0.7)" }}>{label}</span>
                                    </div>
                                    <span style={{ fontWeight: 700, color: v > 0 ? color : "rgba(var(--blanco-rgb), 0.3)" }}>{v}</span>
                                </div>
                            );
                        })}
                        {d.hold > 0 && (
                            <div className="d-flex justify-content-between gap-3" style={{ marginBottom: 3 }}>
                                <div className="d-flex align-items-center gap-2">
                                    <div style={{ width: 8, height: 8, borderRadius: 2, background: "var(--ambar-600)", flexShrink: 0 }}></div>
                                    <span style={{ color: "rgba(var(--blanco-rgb), 0.7)" }}>En proceso de pago</span>
                                </div>
                                <span style={{ fontWeight: 700, color: "var(--ambar-600)" }}>{d.hold}</span>
                            </div>
                        )}
                        <div style={{ borderTop: "1px solid rgba(var(--blanco-rgb), 0.12)", marginTop: 6, paddingTop: 5 }}>
                            <div className="d-flex justify-content-between">
                                <span style={{ color: "rgba(var(--blanco-rgb), 0.55)", fontSize: 11 }}>Total del día</span>
                                <span style={{ fontWeight: 700 }}>{total + (d.hold || 0)}</span>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, valor, color, icono, sub }) {
    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <div className="mb-3">
                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: `${color}18`, flexShrink: 0 }}>
                        <i className={`bi ${icono} fs-5`} style={{ color }}></i>
                    </div>
                </div>
                <p className="text-secondary fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>{label}</p>
                <h3 className="fw-bold text-dark mb-1">{valor}</h3>
                {sub && <p className="text-secondary mb-0" style={{ fontSize: "0.82rem" }}>{sub}</p>}
            </div>
        </div>
    );
}

// ── Barra de progreso horizontal ──────────────────────────────────────────────
function FilaBarra({ label, total, pct, color }) {
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)", fontWeight: 500 }}>{label}</span>
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>{total.toLocaleString("es-CO")}</span>
                    <span style={{ fontSize: "0.8rem", fontWeight: 700, color, minWidth: 36, textAlign: "right" }}>{pct}%</span>
                </div>
            </div>
            <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 7, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
            </div>
        </div>
    );
}

// ── Panel de embudo (web o app) ───────────────────────────────────────────────
function PanelEmbudo({ titulo, icono, funnel }) {
    const max = Math.max(...funnel.map(e => e.total), 1);
    const iniciaron   = funnel.find(e => e.tipo === "iniciar_reserva")?.total   || 0;
    const completaron = funnel.find(e => e.tipo === "reserva_completada")?.total || 0;
    const tasa = iniciaron > 0 ? Math.round((completaron / iniciaron) * 100) : null;

    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <div className="mb-3">
                    <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.88rem" }}>
                        <i className={`bi ${icono} me-2`} style={{ color: "var(--violeta-mapa)" }}></i>
                        {titulo}
                    </h6>
                </div>

                <div className="d-flex flex-column gap-3">
                    {funnel.map(({ tipo, total }) => {
                        const cfg = CONFIG_EVENTOS[tipo];
                        if (!cfg) return null;
                        const pct = Math.round((total / max) * 100);
                        return (
                            <div key={tipo}>
                                <div className="d-flex align-items-start gap-3 mb-1">
                                    <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 32, height: 32, background: `${cfg.color}12`, marginTop: 1 }}>
                                        <i className={`bi ${cfg.icono}`} style={{ color: cfg.color, fontSize: 14 }}></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between align-items-baseline mb-1">
                                            <span className="fw-semibold" style={{ fontSize: "0.82rem", color: "var(--color-text-primary)" }}>{cfg.label}</span>
                                            <span className="fw-bold ms-3 flex-shrink-0" style={{ fontSize: "1rem", color: cfg.color }}>{total.toLocaleString("es-CO")}</span>
                                        </div>
                                        <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 5, overflow: "hidden" }}>
                                            <div style={{ width: `${pct}%`, height: "100%", background: cfg.color, borderRadius: 999, transition: "width 0.6s ease" }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {tasa !== null && (
                    <div className="rounded-3 p-3 mt-3 d-flex align-items-center gap-3"
                        style={{ background: "rgba(var(--azul-logo-claro-rgb), 0.06)", border: "1px solid rgba(var(--azul-logo-claro-rgb), 0.12)" }}>
                        <i className="bi bi-percent" style={{ color: "var(--azul-logo-claro)", fontSize: 16 }}></i>
                        <div>
                            <div className="fw-bold" style={{ fontSize: "1rem", color: "var(--color-text-primary)" }}>{tasa}%</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--color-text-secondary)" }}>
                                Tasa de conversión — de quienes iniciaron, {tasa}% completaron el pago
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Embudo de eventos de reserva ──────────────────────────────────────────────
function SeccionEventosReserva({ eventosFunnelWeb, eventosFunnelApp }) {
    return (
        <div className="mt-4">
            <div className="mb-3">
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                    <i className="bi bi-funnel-fill me-2" style={{ color: "var(--violeta-mapa)" }}></i>
                    Embudo de reservas — últimos 30 días
                </h6>
                <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                    Eventos registrados internamente en el flujo de pago.
                </p>
            </div>
            <div className="row g-3">
                <div className="col-12 col-md-6">
                    <PanelEmbudo titulo="Web" icono="bi-globe2" funnel={eventosFunnelWeb} />
                </div>
                <div className="col-12 col-md-6">
                    <PanelEmbudo titulo="App" icono="bi-phone-fill" funnel={eventosFunnelApp} />
                </div>
            </div>
        </div>
    );
}

// ── Ciclo de vida de la reserva (server-side: webhook Wompi, crons, disputas) ──────────────────
const CONFIG_CICLO_VIDA = {
    booking_confirmed: { label: "Confirmadas",  desc: "Pago aprobado y horario ya definido.",                          color: "var(--azul-logo-claro)", icono: "bi-check2-circle"     },
    booking_completed: { label: "Completadas",  desc: "El servicio ya se prestó — se manda también como conversión diferida a Meta/GA4.", color: "var(--verde-esmeralda)", icono: "bi-check-circle-fill" },
    booking_cancelled: { label: "Canceladas",   desc: "Pago rechazado, o cancelada después de pagada.",               color: "var(--rojo-600)",        icono: "bi-x-circle"          },
    booking_expired:   { label: "Expiradas",    desc: "El hold de checkout venció sin llegar a pagarse.",             color: "var(--gris-400)",        icono: "bi-hourglass-split"   },
    booking_disputed:  { label: "En disputa",   desc: "El comprador abrió un reclamo sobre la reserva.",              color: "var(--ambar-600)",       icono: "bi-flag-fill"         },
};

function SeccionCicloVidaReserva({ cicloVidaReserva }) {
    if (!cicloVidaReserva.some(e => e.total > 0)) return null;
    const max = Math.max(...cicloVidaReserva.map(e => e.total), 1);

    return (
        <div className="mt-4">
            <div className="mb-3">
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                    <i className="bi bi-diagram-3-fill me-2" style={{ color: "var(--violeta-mapa)" }}></i>
                    Ciclo de vida de la reserva — últimos 30 días
                </h6>
                <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                    Eventos registrados desde el servidor (webhook de Wompi, crons, disputas) — no vienen de web ni app específicamente, por eso no se separan por plataforma.
                </p>
            </div>
            <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body p-4">
                    <div className="row g-3">
                        {cicloVidaReserva.map(({ tipo, total }) => {
                            const cfg = CONFIG_CICLO_VIDA[tipo];
                            if (!cfg) return null;
                            const pct = Math.round((total / max) * 100);
                            return (
                                <div className="col-12 col-md-6 col-lg-4" key={tipo}>
                                    <div className="d-flex align-items-start gap-3 mb-1">
                                        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 32, height: 32, background: `${cfg.color}12`, marginTop: 1 }}>
                                            <i className={`bi ${cfg.icono}`} style={{ color: cfg.color, fontSize: 14 }}></i>
                                        </div>
                                        <div className="flex-grow-1 min-w-0">
                                            <div className="d-flex justify-content-between align-items-baseline mb-1">
                                                <span className="fw-semibold" style={{ fontSize: "0.82rem", color: "var(--color-text-primary)" }}>{cfg.label}</span>
                                                <span className="fw-bold ms-2 flex-shrink-0" style={{ fontSize: "1rem", color: cfg.color }}>{total.toLocaleString("es-CO")}</span>
                                            </div>
                                            <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 5, overflow: "hidden" }}>
                                                <div style={{ width: `${pct}%`, height: "100%", background: cfg.color, borderRadius: 999, transition: "width 0.6s ease" }} />
                                            </div>
                                            <div className="mt-1" style={{ fontSize: "0.7rem", color: "var(--gris-400)", lineHeight: 1.3 }}>{cfg.desc}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────
function ReportesReservas() {
    const [dias, setDias]         = useState(30);
    const [datos, setDatos]       = useState(null);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/reportes-reservas", { params: { dias } });
                if (res.data.exito) setDatos(res.data);
            } catch { /* silencioso */ } finally { setCargando(false); }
        })();
    }, [dias]);

    const serie             = datos?.serie             || [];
    const totales           = datos?.totales           || {};
    const ingresos          = datos?.ingresos          || {};
    const ganancia          = datos?.ganancia          || {};
    const eventosFunnelWeb  = datos?.eventosFunnelWeb  || [];
    const eventosFunnelApp  = datos?.eventosFunnelApp  || [];
    const cicloVidaReserva  = datos?.cicloVidaReserva  || [];

    const totalReservas = Object.values(totales).reduce((s, n) => s + n, 0);

    const ESTADOS_TILES = [
        { key: "confirmado", label: "Confirmadas",          color: "var(--azul-logo-claro)", icono: "bi-check2-circle"       },
        { key: "completado", label: "Completadas",          color: "var(--verde-esmeralda)", icono: "bi-patch-check-fill"    },
        { key: "cancelado",  label: "Canceladas",           color: "var(--rojo-600)", icono: "bi-x-circle-fill"       },
        { key: "expirado",   label: "Expiradas",            color: "var(--gris-400)", icono: "bi-clock-history"       },
        { key: "hold",       label: "En proceso de pago",   color: "var(--ambar-600)", icono: "bi-hourglass-split"     },
    ];

    return (
        <div className="container-fluid pb-5">
            {/* Cabecera */}
            <div className="d-flex justify-content-between align-items-start pt-3 pb-2 mb-4 border-bottom flex-wrap gap-3">
                <div>
                    <h1 className="h4 fw-bold text-dark mb-0">Reportes por reservas</h1>
                    <p className="text-muted small mb-0 mt-1">Actividad de reservas y pagos de la plataforma.</p>
                </div>
                <div className="d-flex gap-2">
                    {DIAS_OPS.map(op => (
                        <button
                            key={op.key}
                            className={`btn btn-sm rounded-3 fw-semibold ${dias === op.key ? "btn-dark" : "btn-outline-secondary"}`}
                            onClick={() => setDias(op.key)}
                        >
                            {op.label}
                        </button>
                    ))}
                </div>
            </div>

            {cargando ? (
                <div className="text-center py-5 text-muted">
                    <span className="spinner-border spinner-border-sm me-2"></span>Cargando...
                </div>
            ) : (
                <>
                    {/* ── Tiles de estados ── */}
                    <div className="row g-3 mb-4">
                        {ESTADOS_TILES.map(({ key, label, color, icono }) => (
                            <div key={key} className="col-6 col-md-4 col-lg-2" style={{ minWidth: 0 }}>
                                <StatTile
                                    label={label}
                                    valor={(totales[key] || 0).toLocaleString("es-CO")}
                                    color={color}
                                    icono={icono}
                                />
                            </div>
                        ))}
                        <div className="col-6 col-md-4 col-lg-2" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Total reservas"
                                valor={totalReservas.toLocaleString("es-CO")}
                                color="var(--gris-700)"
                                icono="bi-calendar-check"
                            />
                        </div>
                    </div>

                    {/* ── Ingresos del período ── */}
                    {(ingresos.confirmado > 0 || ingresos.completado > 0) && (
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-6">
                                <StatTile
                                    label={`Ingresos confirmados (últimos ${dias} días)`}
                                    valor={fmtPeso(ingresos.confirmado)}
                                    color="var(--azul-logo-claro)"
                                    icono="bi-credit-card-fill"
                                    sub="Reservas confirmadas pendientes de ejecución"
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <StatTile
                                    label={`Ingresos completados (últimos ${dias} días)`}
                                    valor={fmtPeso(ingresos.completado)}
                                    color="var(--verde-esmeralda)"
                                    icono="bi-cash-coin"
                                    sub="Servicios ya ejecutados"
                                />
                            </div>
                        </div>
                    )}

                    {/* ── Gráfica de barras ── */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                                <div>
                                    <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-bar-chart-fill me-2" style={{ color: "var(--azul-logo-claro)" }}></i>
                                        Reservas por día — últimos {dias} días
                                    </h6>
                                    <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                                        Cada barra muestra el volumen de reservas según su estado final del día.
                                    </p>
                                </div>
                                {/* Leyenda */}
                                <div className="d-flex flex-wrap gap-3">
                                    {SERIES.map(s => (
                                        <div key={s.key} className="d-flex align-items-center gap-1">
                                            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }}></div>
                                            <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>{s.label}</span>
                                        </div>
                                    ))}
                                    <div className="d-flex align-items-center gap-1">
                                        <div style={{ width: 10, height: 10, borderRadius: 2, background: "var(--ambar-600)", flexShrink: 0 }}></div>
                                        <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>En proceso de pago (en tooltip)</span>
                                    </div>
                                </div>
                            </div>
                            {serie.length > 0 ? <StackedBarChart serie={serie} dias={dias} /> : (
                                <p className="text-muted small mb-0 text-center py-4">Sin reservas en el período.</p>
                            )}
                        </div>
                    </div>

                    {/* ── Ganancia neta real (últimos n días) ── */}
                    {ganancia.bruta > 0 && (
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-body p-4">
                                <h6 className="fw-bold text-dark mb-1">
                                    <i className="bi bi-wallet2 me-2 text-success"></i>
                                    Ganancia neta real (últimos {dias} días)
                                </h6>
                                <p className="text-secondary mb-3" style={{ fontSize: "0.8rem" }}>
                                    La comisión bruta de reservas confirmadas/completadas no es lo que
                                    realmente nos queda — Wompi descuenta retención en la fuente en cada
                                    cobro, y una comisión aparte por cada desembolso ya enviado a un vendedor.
                                </p>
                                <div className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--color-track-bg)" }}>
                                        <span className="text-dark" style={{ fontSize: "0.88rem" }}>Comisión bruta</span>
                                        <span className="fw-semibold text-dark">{fmtPeso(ganancia.bruta)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--color-track-bg)" }}>
                                        <span className="text-secondary" style={{ fontSize: "0.88rem" }}>
                                            − Retención en la fuente <span style={{ fontSize: "0.75rem" }}>(estimado, Wompi la descuenta del cobro)</span>
                                        </span>
                                        <span className="fw-semibold" style={{ color: "var(--rojo-600)" }}>− {fmtPeso(ganancia.retencionFuente)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--color-track-bg)" }}>
                                        <span className="text-secondary" style={{ fontSize: "0.88rem" }}>
                                            − Pagos a terceros <span style={{ fontSize: "0.75rem" }}>(comisión Wompi al desembolsar a vendedores)</span>
                                        </span>
                                        <span className="fw-semibold" style={{ color: "var(--rojo-600)" }}>− {fmtPeso(ganancia.pagosTerceros)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center pt-3">
                                        <span className="fw-bold text-dark">Ganancia neta real</span>
                                        <span className="fw-bold" style={{ fontSize: "1.15rem", color: "var(--verde-bootstrap)" }}>{fmtPeso(ganancia.neta)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Distribución por estado ── */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.92rem" }}>
                                <i className="bi bi-pie-chart-fill me-2" style={{ color: "var(--violeta-mapa)" }}></i>
                                Distribución total por estado
                            </h6>
                            <div className="d-flex flex-column gap-3">
                                {[
                                    { key: "confirmado", label: "Confirmadas",        color: "var(--azul-logo-claro)" },
                                    { key: "completado", label: "Completadas",        color: "var(--verde-esmeralda)" },
                                    { key: "cancelado",  label: "Canceladas",         color: "var(--rojo-600)" },
                                    { key: "hold",       label: "En proceso de pago", color: "var(--ambar-600)" },
                                    { key: "expirado",   label: "Expiradas",          color: "var(--gris-400)" },
                                ].map(({ key, label, color }) => {
                                    const v   = totales[key] || 0;
                                    const pct = totalReservas > 0 ? Math.round((v / totalReservas) * 100) : 0;
                                    return <FilaBarra key={key} label={label} total={v} pct={pct} color={color} />;
                                })}
                            </div>
                        </div>
                    </div>

                    {/* ── Embudo de eventos ── */}
                    <SeccionEventosReserva eventosFunnelWeb={eventosFunnelWeb} eventosFunnelApp={eventosFunnelApp} />

                    {/* ── Ciclo de vida de la reserva ── */}
                    <SeccionCicloVidaReserva cicloVidaReserva={cicloVidaReserva} />
                </>
            )}
        </div>
    );
}

export default ReportesReservas;
